import { useCallback, useMemo, useRef, useState } from 'react'

/**
 * Multi-row tick/checkbox selection (Access `checked_row`) cho list page có
 * thao tác multi-row: multi-Delete, Tick All, bulk action theo tick (Truck
 * Status, Declaration Date, in gộp PSS, Change Bank, Send VAT...).
 *
 * Độc lập với focus-row single-select (`useRowSelection`) — dùng SONG SONG:
 * checkbox cột đầu tick nhiều dòng cho action multi, còn click dòng vẫn focus
 * đúng 1 dòng cho Edit/Copy. Giữ tick trong `Set<T>` để `toggleAll` /
 * `allVisibleSelected` tính theo đúng tập đang nhìn thấy.
 *
 * - `visibleRows`: danh sách dòng ĐANG hiển thị (đã lọc/search) — dùng cho
 *   `toggleAll`, `allVisibleSelected`, `someVisibleSelected`, và (mặc định)
 *   `selectedRows`.
 * - `getId`: lấy PK của 1 dòng (numeric hoặc string).
 * - `allRows` (optional): superset để resolve `selectedRows`. Truyền khi page
 *   muốn action giữ cả dòng đã tick nhưng đang bị lọc ẩn (vd Moving / Sampling
 *   resolve theo `baseRows`); bỏ trống → resolve theo `visibleRows` (vd Good
 *   Allocation / Pay Data / Invoice — tick trên dòng ẩn bị loại khỏi action).
 *
 * @example
 *   const sel = useRowMultiSelection(filteredRows, (r) => r.id)
 *   // header select-all
 *   <Checkbox checked={sel.allVisibleSelected}
 *     indeterminate={sel.someVisibleSelected && !sel.allVisibleSelected}
 *     onChange={sel.toggleAll} />
 *   // per-row
 *   <Checkbox checked={sel.isSelected(row.id)} onChange={() => sel.toggle(row.id)} />
 *   // action
 *   const ids = sel.selectedRows.map((r) => r.id)
 *   sel.clear()
 */
export function useRowMultiSelection<Row, T extends number | string = number>(
  visibleRows: readonly Row[],
  getId: (row: Row) => T,
  allRows?: readonly Row[],
) {
  const [selectedIds, setSelectedIds] = useState<Set<T>>(() => new Set<T>())

  // `getId` thường là arrow inline (identity đổi mỗi render) → giữ qua ref để
  // các memo/callback bên dưới KHÔNG recompute thừa mỗi render.
  const getIdRef = useRef(getId)
  getIdRef.current = getId

  const toggle = useCallback((id: T) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  /** Bỏ tick 1 tập id (vd sau khi xoá xong các dòng đã tick). */
  const deselect = useCallback((ids: Iterable<T>) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) next.delete(id)
      return next
    })
  }, [])

  const clear = useCallback(() => setSelectedIds(new Set<T>()), [])

  const isSelected = useCallback((id: T) => selectedIds.has(id), [selectedIds])

  const allVisibleSelected = useMemo(
    () => visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(getIdRef.current(r))),
    [visibleRows, selectedIds],
  )
  const someVisibleSelected = useMemo(
    () => visibleRows.some((r) => selectedIds.has(getIdRef.current(r))),
    [visibleRows, selectedIds],
  )

  // Tick-all header: quyết định add/remove theo tập `prev` mới nhất (tránh stale
  // closure), phạm vi = visibleRows.
  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      const gid = getIdRef.current
      const all = visibleRows.length > 0 && visibleRows.every((r) => next.has(gid(r)))
      if (all) visibleRows.forEach((r) => next.delete(gid(r)))
      else visibleRows.forEach((r) => next.add(gid(r)))
      return next
    })
  }, [visibleRows])

  const sourceRows = allRows ?? visibleRows
  const selectedRows = useMemo(
    () => sourceRows.filter((r) => selectedIds.has(getIdRef.current(r))),
    [sourceRows, selectedIds],
  )

  return {
    selectedIds,
    setSelectedIds,
    count: selectedIds.size,
    selectedRows,
    isSelected,
    toggle,
    toggleAll,
    allVisibleSelected,
    someVisibleSelected,
    clear,
    deselect,
  }
}
